import TreatmentStatusSelector from './TreatmentStatusSelector'
import Grid from '@material-ui/core/Grid'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import useEffectAsync from '../../../../hooks/useEffectAsync'
import { client } from '../../../../client'
import { LoadingIndicator } from '../../../../components/LoadingIndicator'
import { useNotificationBar } from '../../../../components/NotificationBar'
import { TFunnelStatusOption } from './types'

const TreatmentStatusSection = ({
  funnel,
  programId,
  hideStatusNames
}: {
  funnel?: { status: { id: number }; id: number }
  programId?: number | string
  hideStatusNames?: string[]
}) => {
  const { t } = useTranslation('treatmentStatusSelector')
  const [funnelStatusOptions, setFunnelStatusOptions] = useState<
    TFunnelStatusOption[]
  >([])

  const [statusSelectorValue, setStatusSelectorValue] = useState<number | undefined>(
    funnel?.status?.id
  )
  const { showMessage, NotificationBar } = useNotificationBar(undefined)

  const handleStatusChange = async ({
    funnelId,
    statusId
  }: {
    funnelId?: number
    statusId: number
  }) => {
    let ok = false
    if (!funnelId && programId) {
      try {
        const ensured = await client.ensureFunnelForProgram(Number(programId))
        if (ensured?.status === 'success') {
          const resp = await client.updateFunnelStatusByTreatmentProgram(
            Number(programId),
            statusId
          )
          ok = resp?.status === 'success'
        }
      } catch {
        ok = false
      }
    } else if (funnelId) {
      const data = await client.updateFunnelStatus(funnelId, statusId)
      ok = data.status === 'success'
    }

    if (!ok) {
      showMessage(t('errorMessage'), 'error')
      return
    }
    setStatusSelectorValue(statusId)
    window.location.reload() // sync state with status log TODO: ideally we would like to revalidate (and importantly - re-run) proper queries
  }

  useEffectAsync(async () => {
    // this handles optimistic updates and rollbacks if the incorrect value was set
    setStatusSelectorValue(funnel?.status?.id)

    // Fetch all status options and then filter. If we only get the 'ASSIGNED'
    // options, this may result in a blank label, because we didn't fetch it.
    const options = (await client.getFunnelStatusOptions())
      .filter((option) => !option.isLegacy)
      .filter((option) =>
        Array.isArray(hideStatusNames)
          ? !hideStatusNames.includes(option.name)
          : true
      )
    // Prepend placeholder for legacy cases without a funnel
    const withPlaceholder: TFunnelStatusOption[] = [
      {
        id: -1,
        isLegacy: false,
        isVisibleByDefault: false,
        name: '---',
        order: -1,
        stage: 'ASSIGNED'
      },
      ...options
    ]
    setFunnelStatusOptions(withPlaceholder)
  }, [statusSelectorValue, hideStatusNames, funnel?.status?.id])

  if (!funnelStatusOptions?.length) {
    return <LoadingIndicator />
  }

  return (
    <>
      <Grid container direction="row">
        <Grid item>
          <TreatmentStatusSelector
            options={funnelStatusOptions}
            value={statusSelectorValue}
            onChange={(statusId: number) =>
              handleStatusChange({
                funnelId: funnel?.id,
                statusId
              })
            }
          />
        </Grid>
      </Grid>
      {NotificationBar}
    </>
  )
}

export default TreatmentStatusSection
